import React, { useCallback, useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { batchPrintOtherForms } from '../../../../../store/batches/print/batches.print.actions';
import {
  labelQueArchiveBatches,
  labelQuePrintLabelStart,
  labelQuePrintOrderSummary,
  labelQuePrintPackingSlipStart,
  labelQuePrintPickListStart,
  labelQuePrintShipmentManifestStart,
} from '../../../../../store/label-que/label-que.actions';
import { checkDoesBatchHaveOtherForms } from '../../../../../store/batches/batches.api';
import { messages } from './messages';
import Global from '../../../../../store/global-selectors';
import { LABEL_SERVICE_BATCH_STATUS_IDS } from '../../../../../constants/label-batch-status-ids';
import { MarkBatchAsPrintedContext } from '../../context/mark-batch-as-printed';

export const ProcessingHeaderActions = ({ batchId, className = '', isProcessing = false }) => {
  const { printDate, onMarkAsPrintedChange } = useContext(MarkBatchAsPrintedContext);
  const [hasOtherForms, setHasOtherForms] = useState(false);
  const dispatch = useDispatch();

  const onArchiveBatch = useCallback(() => {
    dispatch(labelQueArchiveBatches([batchId]));
  }, [batchId, dispatch]);

  const onPrintLabels = useCallback(() => {
    dispatch(labelQuePrintLabelStart(batchId));
    if (!printDate) {
      onMarkAsPrintedChange();
    }
  }, [batchId, dispatch, printDate, onMarkAsPrintedChange]);

  const onPrintPackingSlips = useCallback(() => {
    dispatch(labelQuePrintPackingSlipStart(batchId));
  }, [batchId, dispatch]);

  const onPrintOrderSummary = useCallback(() => {
    dispatch(labelQuePrintOrderSummary(batchId));
  }, [batchId, dispatch]);

  const onPrintPickList = useCallback(() => {
    dispatch(labelQuePrintPickListStart(batchId));
  }, [batchId, dispatch]);

  const onPrintShipmentManifest = useCallback(() => {
    dispatch(labelQuePrintShipmentManifestStart(batchId));
  }, [batchId, dispatch]);

  const handleOnPrintOtherForms = useCallback(() => {
    dispatch(batchPrintOtherForms(batchId));
  }, [batchId, dispatch]);

  const checkHasOtherForms = useCallback(async () => {
    try {
      const { data } = await checkDoesBatchHaveOtherForms(batchId);
      setHasOtherForms(data.hasOtherForms);
    } catch {
      setHasOtherForms(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (!isProcessing) {
      checkHasOtherForms();
    }
  }, [checkHasOtherForms, isProcessing]);

  const batchStatusId = useSelector((state) =>
    Global.selectBatchesLabelBatchStatusModalStatusByBatchId(state, {
      batchId,
    }),
  );
  const isBatchArchived = batchStatusId === LABEL_SERVICE_BATCH_STATUS_IDS.ARCHIVED;

  return (
    <div className={className}>
      <Button disabled={isProcessing} onClick={onPrintLabels} theme='primary'>
        <FormattedMessage {...messages.printLabels} />
      </Button>
      <Button disabled={isProcessing} onClick={onPrintPackingSlips} theme='tertiary'>
        <FormattedMessage {...messages.printPackingSlips} />
      </Button>
      <DropdownMenu disabled={isProcessing}>
        <DropdownMenuToggler theme='tertiary'>
          <FormattedMessage {...messages.otherDocs} />
        </DropdownMenuToggler>
        <DropdownMenuItem onClick={onPrintOrderSummary}>
          <FormattedMessage {...messages.printOrderSummary} />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onPrintPickList}>
          <FormattedMessage {...messages.printPickList} />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onPrintShipmentManifest}>
          <FormattedMessage {...messages.printShipmentManifest} />
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!hasOtherForms} onClick={handleOnPrintOtherForms}>
          <FormattedMessage {...messages.otherForms} />
        </DropdownMenuItem>
      </DropdownMenu>
      <Button disabled={isProcessing || isBatchArchived} onClick={onArchiveBatch} theme='tertiary'>
        <FormattedMessage {...messages.archiveBatch} />
      </Button>
    </div>
  );
};

ProcessingHeaderActions.propTypes = {
  batchId: PropTypes.string,
  className: PropTypes.string,
  isProcessing: PropTypes.bool,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
};
